/**
 * Scoring calculation logic
 */

import type { ClickResult } from '../utils/types';
import {
  PERFECT_SCORE,
  BASE_PIXEL_TO_POINT_RATIO,
  RATIO_INCREASE_INTERVAL,
  RATIO_INCREASE_AMOUNT,
  MAX_PIXEL_TO_POINT_RATIO,
  FEEDBACK_PERFECT,
  FEEDBACK_EXCELLENT,
  FEEDBACK_GOOD,
  FEEDBACK_NICE,
  FEEDBACK_CLOSE,
} from '../utils/constants';

/**
 * Calculates the pixel-to-point ratio based on current level
 * Ratio INCREASES every 5 levels for harder scoring: 0.5 -> 0.75 -> 1.0 -> ... -> 2.0 max
 */
export function calculatePixelToPointRatio(level: number): number {
  const increaseCount = Math.floor((level - 1) / RATIO_INCREASE_INTERVAL);
  const ratio = BASE_PIXEL_TO_POINT_RATIO + (increaseCount * RATIO_INCREASE_AMOUNT);
  return Math.min(ratio, MAX_PIXEL_TO_POINT_RATIO);
}

/**
 * Calculates points earned based on distance from center
 * Perfect center = 100 points, with penalty based on distance
 */
export function calculatePoints(distance: number, pixelToPointRatio: number): number {
  if (distance === 0) {
    return PERFECT_SCORE;
  }

  const penalty = Math.floor(distance * pixelToPointRatio);
  const points = Math.max(0, PERFECT_SCORE - penalty);
  return points;
}

/**
 * Determines if the click results in a strike
 * Threshold decreases with level for progressive difficulty
 */
export function isStrike(distance: number, strikeThreshold: number): boolean {
  return distance > strikeThreshold;
}

/**
 * Generates feedback message based on distance from center
 * Uses dynamic strike threshold
 */
export function getFeedbackMessage(distance: number, strikeThreshold: number): string {
  if (distance === 0) {
    return FEEDBACK_PERFECT;
  } else if (distance < 5) {
    return FEEDBACK_EXCELLENT;
  } else if (distance < 15) {
    return FEEDBACK_GOOD;
  } else if (distance < 30) {
    return FEEDBACK_NICE;
  } else if (distance <= strikeThreshold) {
    return FEEDBACK_CLOSE;
  }
  return '';
}

/**
 * Calculates the complete result of a click
 */
export function calculateClickResult(
  distance: number,
  level: number,
  strikeThreshold?: number
): ClickResult {
  // Use provided threshold or calculate from level
  const threshold = strikeThreshold ?? require('./difficulty').calculateStrikeThreshold(level);
  const strike = isStrike(distance, threshold);

  // Strikes get 0 points and no feedback
  if (strike) {
    return {
      distance,
      isStrike: true,
      points: 0,
      feedback: '',
    };
  }

  // Non-strikes: calculate points and feedback
  const pixelToPointRatio = calculatePixelToPointRatio(level);
  const points = calculatePoints(distance, pixelToPointRatio);
  const feedback = getFeedbackMessage(distance, threshold);

  return {
    distance,
    isStrike: false,
    points,
    feedback,
  };
}
